/**
 * Reports › Freight and Reports › Margin with freight (TASK-0258, DROP-2b). Freight is what it cost to get goods
 * onto our shelf (drop-off, pallets, backhaul, the vendors' own freight), shown beside cost and never in it:
 * margins elsewhere use cost; these reports add freight. The overhead view puts the store's freight next to its
 * sales and goods for the same days (owner decision 8).
 */
import { Info } from 'lucide-react';
import { money, percent, qty } from '@/lib/format';
import { errorMessage } from '@/lib/api/client';
import { ChipGroup } from '@/ui/Filters';
import { Callout } from '@/ui/Callout';
import { ErrorState, Panel, Skeleton, Stat, StatRow } from '@/ui/Panel';
import { useFreightReport, useMarginReport } from '@/features/buying/freight/api';
import { FreightTable } from '@/features/buying/freight/ThisMonthPanel';
import type {
  FreightGroupBy,
  MarginDepartment,
  MarginRow,
} from '@/features/buying/freight/types';

export const FREIGHT_GROUPS: readonly FreightGroupBy[] = [
  'location',
  'vendor',
  'month',
  'trip',
];

const GROUP_LABEL: Record<FreightGroupBy, string> = {
  location: 'Location',
  vendor: 'Vendor',
  month: 'Month',
  trip: 'Trip',
};

export const FREIGHT_NOTE = 'Margins elsewhere use cost; this report adds freight.';

export function FreightReportView({
  from,
  to,
  group,
  onGroup,
}: {
  from: string;
  to: string;
  group: FreightGroupBy;
  onGroup: (g: FreightGroupBy) => void;
}) {
  const q = useFreightReport(from, to, group);
  const o = q.data?.overhead;
  return (
    <>
      <Callout tone="neutral" icon={<Info />} title="Freight beside cost">
        {FREIGHT_NOTE} Billed: placed bills. est.: from each location&apos;s and
        vendor&apos;s charges, not billed yet. Not placed: bill lines nobody placed. Not
        on an order: placed with no order to reach. Orders count by their drop day.
      </Callout>
      {q.isError ? (
        <ErrorState
          title="The freight report did not load"
          message={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      ) : !o ? (
        <Skeleton className="h-24" />
      ) : (
        <>
          <StatRow className="xl:grid-cols-6">
            <Stat label="Freight" value={money(o.freightCents)} />
            <Stat label="Billed" value={money(o.billedCents)} />
            <Stat label="est." value={money(o.estCents)} sub="not billed yet" />
            <Stat
              label="Not placed"
              value={money(o.notPlacedCents)}
              tone={o.notPlacedCents !== 0 ? 'warn' : undefined}
            />
            <Stat
              label="% of goods received"
              value={o.freightPctOfGoods === null ? '—' : percent(o.freightPctOfGoods)}
              sub={money(o.goodsCents)}
            />
            <Stat
              label="% of sales (overhead)"
              value={o.freightPctOfSales === null ? '—' : percent(o.freightPctOfSales)}
              sub={money(o.salesCents)}
            />
          </StatRow>
          <div className="flex items-center gap-2">
            <span className="text-2xs text-ink-faint tracking-wider uppercase">By</span>
            <ChipGroup
              value={group}
              onChange={onGroup}
              options={FREIGHT_GROUPS.map((g) => ({ value: g, label: GROUP_LABEL[g] }))}
            />
          </div>
          <FreightTable
            title={`Freight by ${GROUP_LABEL[group].toLowerCase()}`}
            rows={q.data?.rows}
            pending={q.isPending}
            label={GROUP_LABEL[group]}
          />
        </>
      )}
    </>
  );
}

export function MarginReportView({
  from,
  to,
  departmentId,
  onDepartment,
}: {
  from: string;
  to: string;
  departmentId: number | null;
  onDepartment: (id: number | null) => void;
}) {
  const q = useMarginReport(from, to, departmentId);
  const data = q.data;
  const totals = (data?.departments ?? []).reduce(
    (t, d) => ({
      sales: t.sales + d.salesCents,
      freight: t.freight + d.freightCents,
      est: t.est + d.freightEstCents,
      atCost: t.atCost + d.marginAtCostCents,
      withFreight: t.withFreight + d.marginWithFreightCents,
    }),
    { sales: 0, freight: 0, est: 0, atCost: 0, withFreight: 0 }
  );
  const pct = (n: number) => (totals.sales > 0 ? percent((n * 100) / totals.sales) : '—');
  return (
    <>
      <Callout tone="neutral" icon={<Info />} title="Margin with freight">
        {FREIGHT_NOTE} Cost is the product&apos;s current cost (as the Financial report
        uses). Freight per unit is the average over its receipts in the 90 days to the
        last day shown
        {data ? ` (${data.freightWindow.from} to ${data.freightWindow.to})` : ''}; a
        product with no receipt then shows freight as not known. Freight not billed yet is
        an estimate and says est.
      </Callout>
      {q.isError ? (
        <ErrorState
          title="The margin report did not load"
          message={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      ) : !data ? (
        <Skeleton className="h-24" />
      ) : (
        <>
          <StatRow>
            <Stat label="Sales" value={money(totals.sales)} />
            <Stat
              label="Freight on what sold"
              value={money(totals.freight)}
              sub={totals.est !== 0 ? `incl. ${money(totals.est)} est.` : 'billed'}
            />
            <Stat
              label="Margin at cost"
              value={money(totals.atCost)}
              sub={pct(totals.atCost)}
            />
            <Stat
              label="Margin with freight"
              value={money(totals.withFreight)}
              sub={pct(totals.withFreight)}
              tone={totals.withFreight < 0 ? 'negative' : undefined}
            />
          </StatRow>
          <Panel
            title="By department"
            description={
              departmentId !== null
                ? 'Showing one department'
                : 'Click a department to see its products'
            }
            bleed
          >
            <DeptTable
              rows={data.departments}
              activeId={departmentId}
              onPick={onDepartment}
            />
          </Panel>
          <Panel title="By product" description={`${data.total} products sold`} bleed>
            <ProductTable rows={data.rows} />
          </Panel>
        </>
      )}
    </>
  );
}

function DeptTable({
  rows,
  activeId,
  onPick,
}: {
  rows: MarginDepartment[];
  activeId: number | null;
  onPick: (id: number | null) => void;
}) {
  return (
    <table className="w-full text-xs" data-testid="margin-departments">
      <thead>
        <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
          <th className="px-3 py-1.5 text-left font-medium">Department</th>
          <th className="w-28 px-3 py-1.5 text-right font-medium">Sales</th>
          <th className="w-28 px-3 py-1.5 text-right font-medium">Cost</th>
          <th className="w-24 px-3 py-1.5 text-right font-medium">Freight</th>
          <th className="w-28 px-3 py-1.5 text-right font-medium">Margin at cost</th>
          <th className="w-32 px-3 py-1.5 text-right font-medium">Margin with freight</th>
        </tr>
      </thead>
      <tbody>
        {activeId !== null ? (
          <tr className="border-line-soft border-b">
            <td colSpan={6} className="px-3 py-1.5">
              <button
                type="button"
                className="text-signal-ink text-2xs hover:underline"
                onClick={() => onPick(null)}
              >
                Every department
              </button>
            </td>
          </tr>
        ) : null}
        {rows.map((d) => (
          <tr
            key={String(d.departmentId)}
            className="border-line-soft border-b last:border-0"
          >
            <td className="px-3 py-1.5">
              {d.departmentId !== null && activeId === null ? (
                <button
                  type="button"
                  className="text-signal-ink hover:underline"
                  onClick={() => onPick(d.departmentId)}
                >
                  {d.departmentName}
                </button>
              ) : (
                <span className="text-ink">{d.departmentName}</span>
              )}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(d.salesCents)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(d.cogsCents)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(d.freightCents)}
              <EstNote cents={d.freightEstCents} />
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(d.marginAtCostCents)}{' '}
              <span className="text-ink-faint">{percent(d.marginPct)}</span>
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(d.marginWithFreightCents)}{' '}
              <span className="text-ink-faint">{percent(d.marginWithFreightPct)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** "incl. $1.20 est." under a freight figure that is partly an estimate (an estimate is never shown as a bill). */
function EstNote({ cents, perUnit }: { cents: number; perUnit?: string | null }) {
  if (cents === 0) return null;
  const text =
    perUnit != null
      ? `incl. ${money(Math.round(Number(perUnit)))} est.`
      : `incl. ${money(cents)} est.`;
  return <div className="text-2xs text-ink-faint">{text}</div>;
}

function ProductTable({ rows }: { rows: MarginRow[] }) {
  return (
    <table className="w-full text-xs" data-testid="margin-products">
      <thead>
        <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
          <th className="px-3 py-1.5 text-left font-medium">Product</th>
          <th className="w-20 px-3 py-1.5 text-right font-medium">Sold</th>
          <th className="w-24 px-3 py-1.5 text-right font-medium">Sales</th>
          <th className="w-20 px-3 py-1.5 text-right font-medium">Cost</th>
          <th className="w-24 px-3 py-1.5 text-right font-medium">Freight / unit</th>
          <th className="w-28 px-3 py-1.5 text-right font-medium">Margin at cost</th>
          <th className="w-32 px-3 py-1.5 text-right font-medium">Margin with freight</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.productId} className="border-line-soft border-b last:border-0">
            <td className="text-ink px-3 py-1.5">
              {r.name}
              {r.departmentName ? (
                <span className="text-2xs text-ink-faint"> · {r.departmentName}</span>
              ) : null}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {qty(r.unitsSold)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(r.salesCents)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(r.costCents)}
            </td>
            <td
              className="px-3 py-1.5 text-right font-mono tabular-nums"
              title={
                r.freightPerUnitExact
                  ? `${(Number(r.freightPerUnitExact) / 100).toFixed(4)} a unit`
                  : undefined
              }
            >
              {r.freightKnown ? (
                <>
                  {money(r.freightPerUnitCents)}
                  <EstNote cents={r.freightEstCents} perUnit={r.estPerUnitExact} />
                </>
              ) : (
                <span className="text-ink-faint">not known</span>
              )}
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              {money(r.marginAtCostCents)}{' '}
              <span className="text-ink-faint">{percent(r.marginPct)}</span>
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
              <span
                className={
                  (r.marginWithFreightCents ?? 0) < 0 ? 'text-negative' : undefined
                }
              >
                {money(r.marginWithFreightCents)}
              </span>{' '}
              <span className="text-ink-faint">{percent(r.marginWithFreightPct)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
